import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useColorModeValue, Stack, useToast, Text, Image, Box, Center, Icon, usePrevious } from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { useConversationContext } from "screens/thread/ConversationContext";
import { AutoComplete, AutoCompleteItem } from "@choc-ui/chakra-autocomplete";
import { AutoCompleteSuggestionList } from "./AutoCompleteSuggestionList";
import type { AutoCompleteRefMethods } from "@choc-ui/chakra-autocomplete";
import { useSelector } from "react-redux";
import type { RootState } from "state/rootReducer";
import { v4 as uuid } from "uuid";
import { AutocompleteSuggestionInput } from "./AutocompleteSuggestionInput";
import { useCustomScrollbar } from "hooks/useCustomScrollbar";
import { SuggestionItem } from "./SuggestionItem";
import type { AutocompleteSuggestionWrapperProps } from "./types";
import { isTickerEntitled, useTickerEntitlementConfig } from "screens/thread/useConversationDialogSuggestionLists";
import { ToastMessageContent } from "screens/common/components";
import { useCopyValue } from "hooks/useCopies";
import { useProjectParams } from "hooks";
import dealsIcon from "screens/common/static/misc/deals_icon.svg";
import dealsTickerIcon from "screens/common/static/misc/deals_ticker_icon.svg";
import { MessageContent } from "screens/common/components/MessageContent";
import { IoCloudOffline } from "react-icons/io5";

export const AutocompleteSuggestionWrapper = React.memo(
  ({
    initialText = "",
    suggestedQuestions = [],
    suggestionListType,
    isSubmitDisabled = false,
    showCharliIcon = true,
    showDealFinderIcon = false,
    onSelectOption,
    value,
    onChange,
    onReset,
    isLoading = false,
    onClickSubmit,
    conversationState,
    minWidthList = "32rem",
    marginLeftList,
    disableFilter = false,
    defaultIsOpen = false,
    className,
    externalInputRef,
    inputId,
    canBypassDisabled,
    setCanBypassDisabled,
    onFocus,
    onFocusShowSuggestionList,
    onBlur,
  }: AutocompleteSuggestionWrapperProps) => {
    const isWebsocketConnected = useSelector((state: RootState) => state.websocket.isConnected);
    const inputSubmitColor = useColorModeValue("gray.50", "gray.800");
    const textColor = useColorModeValue("primary.darkGray", "gray.400");
    const blueBgColor = useColorModeValue("blue.100", "blue.700");
    const greenBgColor = useColorModeValue("green.100", "green.700");
    const upgradeBgColor = useColorModeValue("yellow.50", "yellow.800");
    const toast = useToast();
    const {
      onFeedbackModalOpen,
      setFeedbackValue,
      isConversationOpen,
      onConversationClose,
      onConversationOpen,
      setShowConversationMessages,
      showConversationMessages,
      setIsAnotherInputFocused,
      activeFilter,
      setActiveFilter,
      setShouldClearInputValue,
      shouldClearInputValue,
    } = useConversationContext();
    const autocompleteRef = useRef<AutoCompleteRefMethods>();
    const internalInputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const filterContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = externalInputRef || internalInputRef;
    const { pathname } = useLocation();
    const { scrollbarStyle } = useCustomScrollbar(listRef, { width: "3px", barTransparency: 0.1 });
    const entitlementConfig = useTickerEntitlementConfig();
    const { projectId } = useProjectParams();
    const navigate = useNavigate();
    const [showSuggestionList, setShowSuggestionList] = useState(Boolean(defaultIsOpen));
    const isDefaultOpen = useMemo(() => Boolean(defaultIsOpen), [defaultIsOpen]);
    const didWebsocketPreviouslyConnect = usePrevious(isWebsocketConnected);
    const disconnectedBgColor = useColorModeValue("#f9e4dc", "gray.800");

    const isFilterDisabled = useMemo(() => Boolean(disableFilter), [disableFilter]);

    const isListVisible = useCallback(() => {
      return !!(listRef.current && listRef.current.offsetParent !== null);
    }, []);

    const suggestedQuestionsWithId = useMemo(() => {
      return suggestedQuestions.map((question) => ({ ...question, id: uuid() }));
    }, [suggestedQuestions]);

    const filteredSuggestedQuestions = useMemo(() => {
      if (!activeFilter) return suggestedQuestionsWithId;
      return suggestedQuestionsWithId.filter(
        (q) => q.type?.toUpperCase() === activeFilter.toUpperCase() || q.focus?.toUpperCase().includes(activeFilter.toUpperCase())
      );
    }, [suggestedQuestionsWithId, activeFilter]);

    const onClickFeedback = useCallback(() => {
      if (!value) {
        return;
      }
      setFeedbackValue(value);
      onFeedbackModalOpen();
    }, [value, setFeedbackValue, onFeedbackModalOpen]);

    const onHandleConversation = useCallback(() => {
      setShowConversationMessages(!showConversationMessages);
    }, [showConversationMessages, setShowConversationMessages]);

    const handleInputOnClick = useCallback(
      (onOpen: () => void) => {
        showDealFinderIcon && navigate("/deal-finder");

        setIsAnotherInputFocused(true);
        setShowSuggestionList(true);
        onOpen();
        onFocus?.();
        inputRef.current && inputRef.current.focus();
      },
      [showDealFinderIcon, navigate, setIsAnotherInputFocused, onFocus, inputRef]
    );

    // Store the onOpen function in a ref so we can access it in useEffect
    const onOpenRef = useRef<(() => void) | null>(null);

    const handleInputOnFocus = useCallback(
      (onOpen: () => void) => {
        onOpenRef.current = onOpen;

        if (!onFocusShowSuggestionList) return;

        setShowSuggestionList(true);
        onOpen();
        inputRef.current && inputRef.current.focus();
      },
      [inputRef, onFocusShowSuggestionList]
    );

    // This effect will run when onFocusShowSuggestionList changes
    useEffect(() => {
      if (onFocusShowSuggestionList && onOpenRef.current) {
        setShowSuggestionList(true);
        onOpenRef.current();
        inputRef.current && inputRef.current.focus();
      }
    }, [onFocusShowSuggestionList, inputRef]);

    const handleReset = useCallback(() => {
      projectId && onConversationClose();

      setShowSuggestionList(false);
      setCanBypassDisabled?.(false);
      setActiveFilter(null);
      setShouldClearInputValue(true);
      onReset();
      if ((value && value.length === 0) || value === "/") onConversationClose();
      onChange("");
    }, [projectId, onConversationClose, setCanBypassDisabled, setActiveFilter, setShouldClearInputValue, onReset, onChange, value]);

    useEffect(() => {
      if (shouldClearInputValue) {
        onChange("");
        setShouldClearInputValue(false);
      }
    }, [shouldClearInputValue, onChange, setShouldClearInputValue]);

    // Helper function to check if a modal is currently open
    const isModalOpen = useCallback(() => {
      const modalBackdrop = document.querySelector("[data-chakra-modal-backdrop]");
      if (modalBackdrop) return true;

      const modalDialog = document.querySelector('[role="dialog"]');
      if (modalDialog) return true;

      const portalElements = document.querySelectorAll("[data-portal]");
      for (const portal of portalElements) {
        if (portal.querySelector('[role="dialog"]')) return true;
      }

      const activeElement = document.activeElement;
      if (activeElement) {
        const closestModal = activeElement.closest('[role="dialog"]');
        if (closestModal) return true;
      }

      return false;
    }, []);

    useEffect(() => {
      const handleKeyDown = (ev: KeyboardEvent) => {
        const { key } = ev;
        const suggestedQuestion =
          suggestedQuestions.length > 0 &&
          suggestedQuestions.filter(
            (suggestion) => value?.toLocaleLowerCase() && suggestion.question.toLocaleLowerCase().includes(value)
          )[0];

        const isListCurrentlyVisible = isListVisible();

        switch (key) {
          case "Delete":
          case "Backspace":
            if (isModalOpen()) {
              return;
            }
            if (!value || value?.length <= 1) {
              handleReset();
            }
            break;

          case "Enter":
            setShowConversationMessages(true);
            onClickSubmit?.();
            setShowSuggestionList(false);
            setCanBypassDisabled?.(false);
            break;

          case "Tab": {
            if (!isListCurrentlyVisible) {
              return;
            }
            suggestedQuestion && onSelectOption(suggestedQuestion);
            setShowSuggestionList(false);
            setCanBypassDisabled?.(false);
            ev.preventDefault();
            break;
          }

          case " ":
            {
              const doesValueContainEntity = value?.includes(">") || false;
              setShowSuggestionList(doesValueContainEntity);
            }
            break;

          case "Escape":
            handleReset();
            break;

          default:
            break;
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [
      onClickSubmit,
      onReset,
      onChange,
      value,
      suggestedQuestions,
      onSelectOption,
      setCanBypassDisabled,
      showSuggestionList,
      setIsAnotherInputFocused,
      onBlur,
      inputRef,
      isListVisible,
      projectId,
      onConversationClose,
      setActiveFilter,
      setShouldClearInputValue,
      handleReset,
      setShowConversationMessages,
      isModalOpen,
    ]);

    const handleInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
      setShowSuggestionList(true);
      onChange(ev.target.value);
    };

    const handleSubmit = useCallback(() => {
      if (isLoading) {
        return;
      }
      const suggestedQuestion =
        suggestedQuestions.length > 0 &&
        suggestedQuestions.filter((suggestion) => value?.toLocaleLowerCase() && suggestion.question.toLocaleLowerCase().includes(value))[0];

      onClickSubmit && onClickSubmit(suggestedQuestion ? suggestedQuestion : undefined);
      setShowSuggestionList(false);
    }, [isLoading, onClickSubmit, suggestedQuestions, value]);

    const renderSuggestedQuestions = useCallback(
      (questions: typeof suggestedQuestionsWithId) => {
        return questions.map((suggestion) => (
          <AutoCompleteItem
            _focus={{
              bgColor:
                suggestionListType === "tickers" &&
                !isTickerEntitled(
                  suggestion.type?.toUpperCase(),
                  suggestion.focus?.toUpperCase(),
                  entitlementConfig,
                  suggestion.matchFilter?.[0]
                )
                  ? upgradeBgColor
                  : inputSubmitColor,
            }}
            key={`option-${suggestion.id}`}
            value={JSON.stringify({
              question: suggestion.question,
              focus: suggestion.focus,
              matchFilter: suggestion.matchFilter,
              type: suggestion.type,
            })}
            className="ch-autocomplete-suggestion"
            marginInline={0}
            p="1rem"
            borderRadius="none">
            <SuggestionItem
              suggestion={suggestion}
              value={value}
              isDisabled={
                suggestionListType === "tickers" &&
                !isTickerEntitled(
                  suggestion.type?.toUpperCase(),
                  suggestion.focus?.toUpperCase(),
                  entitlementConfig,
                  suggestion.matchFilter?.[0]
                )
              }
              textColor={textColor}
              betaBgColor={blueBgColor}
              suggestionType={suggestionListType}
            />
          </AutoCompleteItem>
        ));
      },
      [entitlementConfig, inputSubmitColor, upgradeBgColor, value, textColor, blueBgColor, suggestionListType]
    );

    const suggestionBorderColor = useColorModeValue("gray.200", "gray.600");

    const renderDealSuggestions = useCallback(
      (questions: typeof suggestedQuestionsWithId) => {
        // Create a grid layout for deal suggestions
        return (
          <Box>
            {questions.map((suggestion) => (
              <AutoCompleteItem
                _focus={{
                  bgColor:
                    suggestionListType === "tickers" &&
                    !isTickerEntitled(
                      suggestion.type?.toUpperCase(),
                      suggestion.focus?.toUpperCase(),
                      entitlementConfig,
                      suggestion.matchFilter?.[0]
                    )
                      ? upgradeBgColor
                      : inputSubmitColor,
                }}
                key={`option-${suggestion.id}`}
                value={JSON.stringify({
                  question: suggestion.question,
                  focus: suggestion.focus,
                  matchFilter: suggestion.matchFilter,
                  type: suggestion.type,
                })}
                className="ch-autocomplete-suggestion"
                marginInline={0}
                p={0}
                borderRadius="md"
                border="1px solid"
                borderColor={suggestionBorderColor}
                overflow="hidden">
                <Stack direction="row" alignItems="center" spacing={3} p={4} width="100%">
                  <Image
                    src={suggestion.question.trim().split(/\s+/).length === 1 ? dealsTickerIcon : dealsIcon}
                    boxSize={suggestion.question.trim().split(/\s+/).length === 1 ? "1.4rem" : "1.5rem"}
                    pb="1px"
                  />
                  <Text fontSize="sm" fontWeight="medium" color={textColor} noOfLines={2}>
                    {suggestion.label}
                  </Text>
                </Stack>
                <Center>
                  <Box
                    borderRadius="4px"
                    py="2px"
                    px="4px"
                    mr="1rem"
                    fontSize="10px"
                    bgColor={suggestion.question.trim().split(/\s+/).length === 1 ? blueBgColor : greenBgColor}
                    color={textColor}>
                    {suggestion.question.trim().split(/\s+/).length === 1 ? "TICKER" : "QUERY"}
                  </Box>
                </Center>
              </AutoCompleteItem>
            ))}
          </Box>
        );
      },
      [suggestionListType, entitlementConfig, upgradeBgColor, inputSubmitColor, suggestionBorderColor, textColor, blueBgColor, greenBgColor]
    );

    const copyUpgradeNotification = useCopyValue("copy_ticker_upgrade_notification");
    const disconnectedMessage = useCopyValue("copy_websocket_disconnected_message") as string;
    const disconnectedMessageDetails = useCopyValue("copy_websocket_disconnected_message_details") as string;
    const handleToast = useCallback(() => {
      toast.closeAll();
      toast({
        render: ({ onClose }) => (
          <ToastMessageContent
            message={copyUpgradeNotification}
            onClick={() => {
              setShowConversationMessages(true);
              onConversationOpen();
              onClose();
            }}
            onClose={onClose}
          />
        ),
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }, [toast, copyUpgradeNotification, setShowConversationMessages, onConversationOpen]);

    return (
      <Stack spacing="9px" width="100%" height="100%">
        <AutoComplete
          ref={autocompleteRef}
          onSelectOption={({ item: { originalValue } }) => {
            if (!isWebsocketConnected) return;
            const suggestedQuestion = JSON.parse(originalValue);
            const isEntitled = isTickerEntitled(
              suggestedQuestion.type,
              suggestedQuestion.focus,
              entitlementConfig,
              suggestedQuestion.matchFilter?.[0]
            );
            if (suggestionListType === "tickers" && !isEntitled) {
              handleToast();
            } else {
              onSelectOption(suggestedQuestion);
            }
          }}
          defaultIsOpen={isDefaultOpen}
          disableFilter={isFilterDisabled}
          freeSolo
          emptyState={false}>
          {({ onOpen }) => (
            <>
              <AutocompleteSuggestionInput
                inputId={inputId}
                value={value}
                initialText={initialText}
                isSubmitDisabled={isSubmitDisabled}
                showCharliIcon={showCharliIcon}
                showDealFinderIcon={showDealFinderIcon}
                isLoading={isLoading}
                className={className}
                inputRef={inputRef}
                canBypassDisabled={canBypassDisabled}
                isConversationOpen={isConversationOpen}
                conversationState={conversationState}
                isWebsocketConnected={isWebsocketConnected}
                handleInputOnClick={handleInputOnClick}
                handleInputOnFocus={handleInputOnFocus}
                handleSubmit={handleSubmit}
                handleInputChange={handleInputChange}
                onHandleConversation={onHandleConversation}
                setIsAnotherInputFocused={setIsAnotherInputFocused}
                onBlur={onBlur}
                onOpen={onOpen}
                onReset={handleReset}
              />
              {!isWebsocketConnected && didWebsocketPreviouslyConnect && (
                <Box
                  position="relative"
                  zIndex={2}
                  backgroundColor={disconnectedBgColor}
                  width="100%"
                  p="0"
                  marginTop="1px!important"
                  minWidth={minWidthList}
                  maxWidth={minWidthList}
                  boxShadow="xl"
                  borderRadius={"2xl"}
                  maxHeight={"350px"}>
                  <Box py={"0.5rem"} px="1rem">
                    <Box color={textColor}>
                      <MessageContent
                        fontSize="sm"
                        message={disconnectedMessage}
                        icon={<Icon as={IoCloudOffline} color="gray.600" boxSize="1.4rem" />}
                        moreDetails={disconnectedMessageDetails}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
              {isWebsocketConnected && (
                <AutoCompleteSuggestionList
                  showSuggestionList={showSuggestionList}
                  suggestionListType={suggestionListType}
                  isWebsocketConnected={isWebsocketConnected}
                  pathname={pathname}
                  value={value}
                  activeFilter={activeFilter || null}
                  setActiveFilter={setActiveFilter}
                  onClickFeedback={onClickFeedback}
                  listRef={listRef}
                  filterContainerRef={filterContainerRef}
                  scrollbarStyle={scrollbarStyle}
                  marginLeftList={marginLeftList}
                  minWidthList={minWidthList}
                  isLoading={isLoading}>
                  {filteredSuggestedQuestions.length > 0 && suggestionListType === "deals"
                    ? renderDealSuggestions(filteredSuggestedQuestions)
                    : renderSuggestedQuestions(filteredSuggestedQuestions)}
                </AutoCompleteSuggestionList>
              )}
            </>
          )}
        </AutoComplete>
      </Stack>
    );
  }
);

export default AutocompleteSuggestionWrapper;
